import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Flex, Loader, Text } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import { MigrationStatus } from 'src/secret-menu/hooks/useCompanies';
import type { SmartCreateData } from 'src/secret-menu/smart-create/types';
import { useAutoBrandFromUrl } from '../hooks/useAutoBrandFromUrl';
import { usePollMigrationStatus } from '../hooks/usePollMigrationStatus';
import { useSmartCreateCompany } from '../hooks/useSmartCreateCompany';
import { useSmartCreateUser } from '../hooks/useSmartCreateUser';

type Phase = 'creating' | 'migrating' | 'creating-user' | 'error';

export default function ProcessingStep({
  data,
  setData,
  nextStep,
  close,
}: StepProps<SmartCreateData>) {
  const [phase, setPhase] = useState<Phase>('creating');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasStarted = useRef(false);
  const hasCreatedUser = useRef(false);

  const { createCompany } = useSmartCreateCompany();
  const migrationStatus = usePollMigrationStatus(data.createdCompanyId, phase === 'migrating');
  const { createUser } = useSmartCreateUser(data.createdCompanyId ?? undefined);
  const { extractFromUrl } = useAutoBrandFromUrl(data.createdCompanyId);

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    async function run() {
      try {
        const companyId = await createCompany({
          name: data.companyName!,
          fca_firm_reference_number: data.frn,
          website: data.website || null,
        });
        setData(prev => ({ ...prev, createdCompanyId: companyId }));
        setPhase('migrating');
      } catch {
        setPhase('error');
        setErrorMessage('Failed to create company.');
      }
    }
    run();
  }, []);

  useEffect(() => {
    if (migrationStatus === MigrationStatus.COMPLETED && !hasCreatedUser.current) {
      hasCreatedUser.current = true;
      setPhase('creating-user');
      async function run() {
        try {
          await createUser({
            firstName: data.firstName,
            lastName: data.lastName,
            emailAddress: data.email,
          });
          if (data.website) {
            extractFromUrl(data.website).catch(() => undefined);
          }
          nextStep();
        } catch {
          setPhase('error');
          setErrorMessage('Failed to create user.');
        }
      }
      run();
    } else if (migrationStatus === MigrationStatus.FAILED) {
      setPhase('error');
      setErrorMessage('Company setup failed.');
    }
  }, [migrationStatus]);

  if (phase === 'error') {
    return (
      <Flex direction="column" flex={1} justify="center" align="center" gap="xl" p="xl">
        <Alert color="red" title="Setup failed">
          {errorMessage}
        </Alert>
        <Button variant="light" onClick={close}>
          Close
        </Button>
      </Flex>
    );
  }

  const statusLabels: Record<Exclude<Phase, 'error'>, string> = {
    creating: 'Creating company…',
    migrating: 'Setting up your company…',
    'creating-user': 'Creating user…',
  };
  const statusLabel = statusLabels[phase as Exclude<Phase, 'error'>];

  return (
    <Flex direction="column" flex={1} justify="center" align="center" gap="md">
      <Loader size="lg" />
      <Text fw={FONT_WEIGHTS.medium} c="gray.8">
        {statusLabel}
      </Text>
    </Flex>
  );
}
